import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Seo } from "components/Seo";
import useAccountData from "hooks/useAccountData";
import { Company } from "models/Company";
import { webShopService } from "services/Services";
import { UrlPath } from "utility/routes";
import { BulletpointsProductModel, ExtraLinksProductModel, InfotextProductModel, LogoProductModel, WebshopOrderModel, WebshopProductModel } from 'models/WebShop';
import { useCart } from 'react-use-cart';
import Checkout from './Checkout';
import BreadcrumbCompany from 'pages/Account/BreadcrumbCompany';
import { getPayloadString } from 'utility/ProductUtility';
import { useOrders } from 'context/useOrderData';

type ShoppingCartFormProps = {
  companyId: string
}


const CheckoutForm = ({ companyId }: ShoppingCartFormProps) => {
  const { fetchSaleStatus } = useOrders();
  const [errors, setErrors] = useState<string[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [sendMsg, setSendMsg] = useState("");
  const [submitForm, setSubmitForm] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(false);
  const [sentOrder, setSentOrder] = useState<WebshopOrderModel | undefined>();
  const [enableForm, setEnableForm] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [email, setEmail] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");

  const { loadingCompanies, getCurrentCompany } = useAccountData();
  const params = useParams();
  const navigate = useNavigate();
  const cart = useCart();

  useEffect(() => {
    const handleEnableFormButton = () => {

      const products = cart.items.filter(p => p.documentId === companyId);
      if (acceptedTerms && email !== "" && email.length > 2 && errors.length === 0 && products.length > 0) {
        setEnableForm(true);
      } else {
        setEnableForm(false);
      }
    };

    handleEnableFormButton();
  }, [acceptedTerms, email, cart.cartTotal, errors]);
  const handleAddressValidation = (addressErrors: string[]) => {
    setErrors((prevErrors) => [...prevErrors.filter(error => !error.includes("Street") && !error.includes("City") && !error.includes("ZIP")), ...addressErrors]);
  };

  const handleEmailValidation = (emailErrors: string[]) => {
    setErrors((prevErrors) => [...prevErrors.filter(error => !error.includes("e-post")), ...emailErrors]);
  };



  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitForm(true);

    let getProductsFromCart = cart.items.filter(item => item.documentId === companyId) as WebshopProductModel[];


    getProductsFromCart.forEach(product => {
      product.payload = getPayloadString(product);
    });

    const order: WebshopOrderModel = {
      id: "",
      documentId: companyId,
      email: email,
      orgNr: company?.companyCode ?? "",
      products: getProductsFromCart
    }

    if (appliedCoupon)
      order.couponCode = appliedCoupon;

    if (errors.length === 0) {
      try {
        const response = await webShopService.addOrder(order);

        if (response !== null && response?.success) {
          getProductsFromCart.forEach(x => {
            cart.removeItem(x.id);
          });
          setSentOrder(response.order);
          fetchSaleStatus(companyId);
        }
        else {
          setSendMsg(response?.message ?? "");
        }

      } catch (error) {
        setSendMsg('Error sending order');
      }
      finally {
        setSubmitForm(false);
      }
    } else {
      setSubmitForm(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!loadingCompanies) {
        const response = await getCurrentCompany(companyId);
        if (response) {
          setCompany(response);
        }
      }
    };
    fetchData();
  }, [loadingCompanies, companyId]);

  // useEffect(() => {
  //   const checkPendingOrder = async () => {
  //     if (company) {
  //       const checkOrder = await webShopService.hasPendingOrder(company.id, company.companyCode);
  //       if (checkOrder) {
  //         setOrder(checkOrder);
  //         setPendingOrder(checkOrder.hasPendingOrder);
  //       }
  //     }
  //   };
  //   checkPendingOrder();
  // }, [company]);

  return (
    <>
      <Seo title="Handlekurv" />
      {
        sentOrder === undefined ? (
          <div className="card mb-3">
            {/* <h1 className="h3">Firmalogo</h1> */}
            <form onSubmit={handleSubmit} className="card-body">
              <BreadcrumbCompany overrideId={companyId} />
              <Checkout onSubmit={handleSubmit}
                submitForm={submitForm}
                email={email}
                onEmailChange={(value: string) => setEmail(value)}
                onEmailValidation={handleEmailValidation}
                onValidation={handleAddressValidation}
                acceptedTerms={acceptedTerms}
                onTermsChange={(value: boolean) => setAcceptedTerms(value)}
                enableForm={enableForm}
                overrideId={companyId}
                appliedCoupon={appliedCoupon}
                setAppliedCoupon={setAppliedCoupon}
              />

            </form>
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              <h1 className="h3">Din bestilling er mottatt</h1>
              <p className="lead"> Faktura og ordrebekreftelse er sendt til {sentOrder.email}</p>
              <p className="text-muted">Informasjonen blir tilgjengelig på nett så fort den er behandlet.</p>
              <div className="cc-scroll-wrapper">
                <div className="table-responsive" id="scrollContainer">
                  <table className="table cc-table cc-table-shoppingcart cc-table-muted mb-4">
                    <tbody><tr>
                      <th>Produkt</th>
                      <th></th>
                      <th>Pris</th>
                    </tr>
                      {sentOrder.products.map((product) =>
                        <tr className="cc-cart-item" id={product.id}>
                          <td>{product.name}</td>
                          <td></td>
                          <td>{product.price},-</td>
                        </tr>)}
                      <tr>
                        <td colSpan={2}><span className="cc-fw-medium">Totalt</span></td>
                        <td><span className="cc-fw-medium">{sentOrder.products.reduce((acc, p) => acc + p.price, 0)},- </span></td>
                      </tr>
                    </tbody></table>


                  <div className="cc-fade-overlay" id="fadeOverlay" style={{ opacity: 0 }}>
                    <button className="cc-scroll-arrow" id="scrollArrow" title="Scroll to end"><span>›</span></button>
                  </div>


                </div>
              </div>
              <p className="mb-0 mt-4">
                <button onClick={() => navigate(UrlPath.MyAccount.Home)} className="btn btn-outline-primary">Tilbake til oversikt</button>
              </p>
            </div>
          </div>
        )}
    </>
  );
};

export default CheckoutForm;